import Flickity from "flickity";
import carouselCellAccessibility from "./carousel-cell-accessibility";

/**
 * Product Carousel Tabs
 * Tabs layout of the Product Carousel section. Only the visible tab panel holds
 * a Flickity instance: hidden panels measure 0 wide, so a panel is built when
 * its tab is selected and destroyed when it is hidden.
 * @param {object} opts
 */
function productsCarouselTabs(opts) {
  const config = {
    dom: {
      carousel: "js-product-carousel-tabs-carousel",
      cell: "js-product-carousel-tabs-cell",
      hint: "js-product-carousel-tabs-hint",
      hintPrev: "js-product-carousel-tabs-hint-prev",
      next: "js-product-carousel-tabs-next",
      panel: "js-product-carousel-tabs-panel",
      prev: "js-product-carousel-tabs-prev",
      section: "js-product-carousel-tabs",
      tab: "js-product-carousel-tabs-tab",
      tabList: "js-product-carousel-tabs-list"
    },
    cls: {
      static: "is-static"
    }
  };
  const c = __.extend(config, opts);

  // resize is handled here so the cells are measured at their natural height
  const flickityOpts = {
    accessibility: false,
    cellAlign: "left",
    contain: true,
    dragThreshold: 5,
    groupCells: true,
    pageDots: true,
    prevNextButtons: true,
    resize: false
  };
  const flickityMobileOpts = {
    ...flickityOpts,
    friction: 0.8,
    selectedAttraction: 0.4
  };

  const cellA11y = carouselCellAccessibility({ cellClass: c.dom.cell });
  let started = false;
  let view;

  function getSections() {
    return [...document.querySelectorAll(`.${c.dom.section}`)];
  }

  function getActivePanel($section) {
    return $section.querySelector(`.${c.dom.panel}:not([hidden])`);
  }

  function getCarousel($panel) {
    return $panel?.querySelector(`.${c.dom.carousel}`);
  }

  // cells stretch to the viewport height, so they are released while Flickity measures them
  function withNaturalHeights($cells, fn) {
    $cells.forEach(($cell) => {
      $cell.style.height = "auto";
    });
    const result = fn();
    $cells.forEach(($cell) => {
      $cell.style.removeProperty("height");
    });

    return result;
  }

  function toggleStatic(flickity) {
    flickity.element.classList.toggle(c.cls.static, flickity.slides.length < 2);
  }

  function destroyCarousel($carousel) {
    if (!$carousel) {
      return;
    }

    delete $carousel.dataset.ready;

    const flickity = Flickity.data($carousel);
    if (!flickity) {
      return;
    }

    flickity.destroy();
    $carousel.classList.remove(c.cls.static);
    cellA11y.clear($carousel.querySelectorAll(`.${c.dom.cell}`));
  }

  // a section hidden at load (the empty cart carousel, for example) is built once it gets a width
  function buildCarousel($panel) {
    const $carousel = getCarousel($panel);
    if (!$carousel) {
      return;
    }

    destroyCarousel($carousel);
    if ($carousel.offsetWidth === 0) {
      return;
    }

    $carousel.dataset.ready = "1";

    const $cells = [...$carousel.querySelectorAll(`.${c.dom.cell}`)];
    if ($cells.length < 2) {
      return;
    }

    const carouselOpts = view === "mobile" ? flickityMobileOpts : flickityOpts;
    const flickity = withNaturalHeights($cells, () => new Flickity($carousel, carouselOpts));

    toggleStatic(flickity);
    flickity.on("resize", () => {
      toggleStatic(flickity);
      cellA11y.sync(flickity);
    });
    cellA11y.bind(flickity);

    window.dispatchEvent(new CustomEvent("product-view-changed"));
  }

  function resizeCarousel($carousel) {
    const flickity = $carousel && Flickity.data($carousel);
    if (!flickity) {
      return;
    }

    withNaturalHeights(flickity.getCellElements(), () => flickity.resize());
    cellA11y.sync(flickity);
  }

  function selectTab($section, $tab) {
    if ($tab.getAttribute("aria-selected") === "true") {
      return;
    }

    const $tabs = [...$section.querySelectorAll(`.${c.dom.tab}`)];
    const $activePanel = getActivePanel($section);
    const $panel = document.getElementById($tab.getAttribute("aria-controls"));

    $tabs.forEach(($el) => {
      const isSelected = $el === $tab;
      $el.setAttribute("aria-selected", isSelected);
      $el.tabIndex = isSelected ? 0 : -1;
    });

    if ($activePanel) {
      destroyCarousel(getCarousel($activePanel));
      $activePanel.hidden = true;
    }
    if (!$panel) {
      return;
    }

    $panel.hidden = false;
    buildCarousel($panel);
  }

  function onTabKeydown(e, $section) {
    if (!["ArrowLeft", "ArrowRight", "End", "Home"].includes(e.key)) {
      return;
    }

    const $tabs = [...$section.querySelectorAll(`.${c.dom.tab}`)];
    const index = $tabs.indexOf(e.target.closest(`.${c.dom.tab}`));
    if (index === -1) {
      return;
    }

    let next = index;
    if (e.key === "ArrowLeft") {
      next = (index - 1 + $tabs.length) % $tabs.length;
    } else if (e.key === "ArrowRight") {
      next = (index + 1) % $tabs.length;
    } else if (e.key === "Home") {
      next = 0;
    } else if (e.key === "End") {
      next = $tabs.length - 1;
    }

    e.preventDefault();
    $tabs[next].focus({ preventScroll: true });
    $tabs[next].scrollIntoView({ block: "nearest", inline: "nearest" });
    selectTab($section, $tabs[next]);
  }

  function bindTabsHint($section) {
    const $list = $section.querySelector(`.${c.dom.tabList}`);
    const $hint = $section.querySelector(`.${c.dom.hint}`);
    const $hintPrev = $section.querySelector(`.${c.dom.hintPrev}`);
    const $next = $section.querySelector(`.${c.dom.next}`);
    const $prev = $section.querySelector(`.${c.dom.prev}`);
    if (!$list || !$hint) {
      return;
    }

    const step = () => Math.max(120, Math.floor($list.clientWidth * 0.9));
    const update = () => {
      const max = $list.scrollWidth - $list.clientWidth;
      const left = Math.round($list.scrollLeft);
      $hint.hidden = max <= 1 || left >= max - 1;
      if ($hintPrev) {
        $hintPrev.hidden = max <= 1 || left <= 1;
      }
    };

    $next?.addEventListener("click", () => {
      $list.scrollBy({ left: step(), behavior: "smooth" });
    });
    $prev?.addEventListener("click", () => {
      $list.scrollBy({ left: -step(), behavior: "smooth" });
    });

    // iOS Safari bounces past the last tab, so scrollLeft is clamped back in range
    $list.addEventListener("scroll", () => {
      const max = $list.scrollWidth - $list.clientWidth;
      if ($list.scrollLeft < 0) {
        $list.scrollLeft = 0;
      } else if ($list.scrollLeft > max) {
        $list.scrollLeft = max;
      }
      update();
    }, { passive: true });

    document.fonts?.ready.then(update);
    __.windowResize(update);
    update();
  }

  function observeVisibility($section) {
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      const $panel = getActivePanel($section);
      const $carousel = getCarousel($panel);

      if ($carousel && $carousel.offsetWidth > 0 && !$carousel.dataset.ready) {
        buildCarousel($panel);
      }
    });

    observer.observe($section);
  }

  function bindSection($section) {
    if ($section.dataset.tabsBound) {
      return;
    }

    $section.dataset.tabsBound = "1";

    const $tabList = $section.querySelector(`.${c.dom.tabList}`);
    if ($tabList) {
      $tabList.addEventListener("click", (e) => {
        const $tab = e.target.closest(`.${c.dom.tab}`);
        if ($tab) {
          selectTab($section, $tab);
        }
      });
      $tabList.addEventListener("keydown", (e) => onTabKeydown(e, $section));
      bindTabsHint($section);
    }

    observeVisibility($section);
  }

  function rebuildActive() {
    getSections().forEach(($section) => {
      const $panel = getActivePanel($section);
      if ($panel) {
        buildCarousel($panel);
      }
    });
  }

  function resizeActive() {
    getSections().forEach(($section) => {
      resizeCarousel(getCarousel(getActivePanel($section)));
    });
  }

  /**
   * On Window Resize
   */
  function onWindowResize() {
    const previousView = view;

    // desktop
    __.mq({
      view: "desktop",
      callback: () => {
        view = "desktop";
      }
    });

    // mobile
    __.mq({
      view: "mobile",
      callback: () => {
        view = "mobile";
      }
    });

    if (view !== previousView) {
      rebuildActive();
    } else {
      resizeActive();
    }
  }

  function start() {
    if (started) {
      return;
    }

    started = true;
    onWindowResize();
    __.windowResize(onWindowResize);
    document.addEventListener("resizeFlickity", resizeActive);
    document.fonts?.ready.then(resizeActive);
  }

  // theme editor: a re-rendered section comes back without listeners or Flickity
  function onSectionLoad(e) {
    const $section = e.target.querySelector(`.${c.dom.section}`);
    if (!$section) {
      return;
    }

    bindSection($section);

    if (!started) {
      start();
      return;
    }

    const $panel = getActivePanel($section);
    if ($panel) {
      buildCarousel($panel);
    }
  }

  // theme editor: selecting a Collection Tab block opens its tab
  function onBlockSelect(e) {
    const $tab = e.target.closest(`.${c.dom.tab}`);
    const $section = e.target.closest(`.${c.dom.section}`);

    if ($tab && $section) {
      selectTab($section, $tab);
    }
  }

  /**
   * Init
   */
  function init() {
    const $sections = getSections();

    $sections.forEach(bindSection);
    if ($sections.length) {
      start();
    }

    if (window.Shopify?.designMode) {
      document.addEventListener("shopify:section:load", onSectionLoad);
      document.addEventListener("shopify:block:select", onBlockSelect);
    }
  }

  return init();
}

export default productsCarouselTabs;
